/**
 * Firebase Client SDK initialization for Web App
 *
 * This module initializes and exports the Firebase Client SDK services
 * used by the web application. It supports both production Firebase
 * and local emulators for development.
 */

import {
  initializeApp,
  getApps,
  getApp,
  type FirebaseApp,
  type FirebaseOptions,
} from 'firebase/app';
import { getAuth, connectAuthEmulator, signInWithCustomToken, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

// Firebase configuration from environment variables
// Using a function to build the config to satisfy TypeScript strict mode
const getFirebaseConfig = (): FirebaseOptions => {
  return {
    apiKey: import.meta.env['VITE_FIREBASE_API_KEY'] ?? '',
    authDomain: import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'] ?? '',
    projectId: import.meta.env['VITE_FIREBASE_PROJECT_ID'] ?? '',
    storageBucket: import.meta.env['VITE_FIREBASE_STORAGE_BUCKET'] ?? '',
    messagingSenderId: import.meta.env['VITE_FIREBASE_MESSAGING_SENDER_ID'] ?? '',
    appId: import.meta.env['VITE_FIREBASE_APP_ID'] ?? '',
  };
};

// Check if we should use emulators
const useEmulators = import.meta.env['VITE_USE_FIREBASE_EMULATORS'] === 'true';

// Emulator configuration (allows running multiple instances and remote access)
const emulatorHost = import.meta.env['VITE_EMULATOR_HOST'] ?? 'localhost';
const emulatorPorts = {
  auth: parseInt(import.meta.env['VITE_EMULATOR_AUTH_PORT'] ?? '9099', 10),
  firestore: parseInt(import.meta.env['VITE_EMULATOR_FIRESTORE_PORT'] ?? '8080', 10),
  storage: parseInt(import.meta.env['VITE_EMULATOR_STORAGE_PORT'] ?? '9199', 10),
  functions: parseInt(import.meta.env['VITE_EMULATOR_FUNCTIONS_PORT'] ?? '5001', 10),
};

// Initialize Firebase App (singleton pattern)
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;

// Track if emulators have been connected
let emulatorsConnected = false;

/**
 * Initialize Firebase services
 * This should be called once at app startup
 */
export const initializeFirebase = (): void => {
  // Initialize app if not already done
  if (getApps().length === 0) {
    const config = getFirebaseConfig();
    app = initializeApp(config);
  } else {
    app = getApp();
  }

  // Get service instances
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app);

  // Connect to emulators in development
  if (useEmulators && !emulatorsConnected) {
    connectAuthEmulator(auth, `http://${emulatorHost}:${emulatorPorts.auth}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, emulatorHost, emulatorPorts.firestore);
    connectStorageEmulator(storage, emulatorHost, emulatorPorts.storage);
    connectFunctionsEmulator(functions, emulatorHost, emulatorPorts.functions);
    emulatorsConnected = true;

    // Log emulator connection in development
    if (import.meta.env['DEV']) {
      console.warn('[Firebase] Connected to emulators at', emulatorHost, emulatorPorts);
    }

    // Expose E2E test helper for Playwright auth setup
    // This allows E2E tests to sign in using custom tokens
    (window as WindowWithE2EHelpers).__e2e_signInWithCustomToken = async (customToken: string) => {
      return signInWithCustomToken(auth, customToken);
    };
  }
};

/**
 * Type definition for window with E2E helpers
 * Only available when running with emulators
 */
interface WindowWithE2EHelpers extends Window {
  __e2e_signInWithCustomToken?: (customToken: string) => Promise<unknown>;
}

// Export getter functions to ensure Firebase is initialized
export const getFirebaseAuth = (): Auth => {
  if (!auth) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return auth;
};

export const getFirebaseDb = (): Firestore => {
  if (!db) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return db;
};

export const getFirebaseStorage = (): FirebaseStorage => {
  if (!storage) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return storage;
};

export const getFirebaseFunctions = (): Functions => {
  if (!functions) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return functions;
};

// Export configuration for reference
export { useEmulators };
export const firebaseConfig = getFirebaseConfig();
